import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, ViewChild, computed, signal } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';

import { MethodsUtils } from '../../../../shared/util/methods.utils';
import { VELOCIDADES_MIDIA } from '../../../utils/chat.constantes';
import { ESPERA_OCULTAR_MS, PASSO_TECLADO_S } from './utils/player-video.constantes';

@Component({
  selector: 'app-player-video',
  standalone: true,
  templateUrl: './player-video.component.html',
  styleUrl: './player-video.component.css'
})
export class PlayerVideoComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) src!: SafeResourceUrl;

  @ViewChild('video', { static: true }) videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLElement>;

  tocando = signal(false);
  carregando = signal(true);
  erro = signal(false);
  duracao = signal(0);
  atual = signal(0);
  carregado = signal(0);
  velocidade = signal(1);
  mudo = signal(false);
  volume = signal(1);
  telaCheia = signal(false);
  emPip = signal(false);
  readonly suportaPip = typeof document !== 'undefined' && !!document.pictureInPictureEnabled;
  arrastando = signal(false);
  controlesVisiveis = signal(true);
  posicaoHover = signal<number | null>(null);

  readonly progresso = computed(() => (this.duracao() > 0 ? Math.min(1, this.atual() / this.duracao()) : 0));
  readonly progressoCarregado = computed(() => (this.duracao() > 0 ? Math.min(1, this.carregado() / this.duracao()) : 0));
  readonly volumeExibido = computed(() => (this.mudo() ? 0 : this.volume()));

  private timerOcultar: ReturnType<typeof setTimeout> | null = null;
  private quadro: number | null = null;
  private sobreControles = false;
  private ultimoPonteiro = 'mouse';

  ngOnChanges(): void {
    this.tocando.set(false);
    this.carregando.set(true);
    this.erro.set(false);
    this.atual.set(0);
    this.duracao.set(0);
    this.carregado.set(0);
    this.mostrarControles();
  }

  ngOnDestroy(): void {
    this.videoRef?.nativeElement.pause();
    this.pararAcompanhamento();
    this.limparTimer();
    if (this.telaCheia()) document.exitFullscreen?.().catch(() => undefined);
    if (this.emPip()) document.exitPictureInPicture?.().catch(() => undefined);
  }

  alternar(event?: Event): void {
    event?.stopPropagation();
    const video = this.videoRef.nativeElement;
    if (this.erro()) return;
    if (video.paused || video.ended) {
      document.querySelectorAll('audio').forEach(audio => audio.pause());
      video.play().catch(() => this.tocando.set(false));
    } else {
      video.pause();
    }
  }

  aoTocar(): void {
    this.tocando.set(true);
    this.acompanhar();
    this.agendarOcultar();
  }

  aoPausar(): void {
    this.tocando.set(false);
    this.pararAcompanhamento();
    this.mostrarControles();
  }

  aoTerminar(): void {
    this.aoPausar();
    this.atual.set(this.duracao());
  }

  aoCarregarMetadados(): void {
    const video = this.videoRef.nativeElement;
    if (Number.isFinite(video.duration)) this.duracao.set(video.duration);
    video.playbackRate = this.velocidade();
  }

  aoMudarDuracao(): void {
    const video = this.videoRef.nativeElement;
    if (Number.isFinite(video.duration)) this.duracao.set(video.duration);
  }

  aoAtualizarTempo(): void {
    if (!this.arrastando()) this.atual.set(this.videoRef.nativeElement.currentTime);
  }

  aoProgredir(): void {
    const video = this.videoRef.nativeElement;
    const { buffered, currentTime } = video;
    for (let i = 0; i < buffered.length; i++) {
      if (buffered.start(i) <= currentTime + 0.5 && buffered.end(i) >= currentTime) {
        this.carregado.set(buffered.end(i));
        return;
      }
    }
  }

  aoFalhar(): void {
    this.erro.set(true);
    this.carregando.set(false);
    this.tocando.set(false);
  }

  private acompanhar(): void {
    this.pararAcompanhamento();
    const passo = () => {
      const video = this.videoRef?.nativeElement;
      if (!video || video.paused) {
        this.quadro = null;
        return;
      }
      if (!this.arrastando()) this.atual.set(video.currentTime);
      this.quadro = requestAnimationFrame(passo);
    };
    this.quadro = requestAnimationFrame(passo);
  }

  private pararAcompanhamento(): void {
    if (this.quadro !== null) cancelAnimationFrame(this.quadro);
    this.quadro = null;
  }

  trocarVelocidade(event: Event): void {
    event.stopPropagation();
    const proxima = VELOCIDADES_MIDIA[(VELOCIDADES_MIDIA.indexOf(this.velocidade()) + 1) % VELOCIDADES_MIDIA.length];
    this.velocidade.set(proxima);
    this.videoRef.nativeElement.playbackRate = proxima;
  }

  rotuloVelocidade(): string {
    return `${String(this.velocidade()).replace('.', ',')}×`;
  }

  alternarSom(event: Event): void {
    event.stopPropagation();
    const video = this.videoRef.nativeElement;
    if (this.mudo() && this.volume() === 0) this.definirVolume(0.6);
    video.muted = !video.muted;
    this.mudo.set(video.muted);
  }

  aoMudarVolume(event: Event): void {
    const valor = Number((event.target as HTMLInputElement).value);
    this.definirVolume(valor);
    const video = this.videoRef.nativeElement;
    video.muted = valor === 0;
    this.mudo.set(video.muted);
  }

  private definirVolume(valor: number): void {
    this.volume.set(valor);
    this.videoRef.nativeElement.volume = valor;
  }

  aoPressionarBarra(event: PointerEvent): void {
    event.stopPropagation();
    if (!this.duracao()) return;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    this.arrastando.set(true);
    this.irParaPonto(event);
  }

  aoMoverBarra(event: PointerEvent): void {
    if (event.pointerType === 'mouse') this.posicaoHover.set(this.proporcaoNoPonto(event));
    if (this.arrastando()) this.irParaPonto(event);
  }

  aoSoltarBarra(event: PointerEvent): void {
    if (!this.arrastando()) return;
    this.irParaPonto(event);
    this.arrastando.set(false);
    this.agendarOcultar();
  }

  aoTeclarBarra(event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    event.stopPropagation();
    this.pular(event.key === 'ArrowRight' ? PASSO_TECLADO_S : -PASSO_TECLADO_S);
  }

  private irParaPonto(event: PointerEvent): void {
    const video = this.videoRef.nativeElement;
    video.currentTime = this.proporcaoNoPonto(event) * this.duracao();
    this.atual.set(video.currentTime);
  }

  private proporcaoNoPonto(event: PointerEvent): number {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    return Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
  }

  private pular(segundos: number): void {
    const video = this.videoRef.nativeElement;
    if (!this.duracao()) return;
    video.currentTime = Math.min(this.duracao(), Math.max(0, video.currentTime + segundos));
    this.atual.set(video.currentTime);
    this.mostrarControles();
    this.agendarOcultar();
  }

  alternarTelaCheia(event?: Event): void {
    event?.stopPropagation();
    const container = this.containerRef.nativeElement;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    } else if (container.requestFullscreen) {
      container.requestFullscreen().catch(() => undefined);
    } else {
      (this.videoRef.nativeElement as HTMLVideoElement & { webkitEnterFullscreen?: () => void }).webkitEnterFullscreen?.();
    }
  }

  @HostListener('document:fullscreenchange')
  aoMudarTelaCheia(): void {
    this.telaCheia.set(document.fullscreenElement === this.containerRef.nativeElement);
  }

  alternarPip(event: Event): void {
    event.stopPropagation();
    const video = this.videoRef.nativeElement;
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch(() => undefined);
      return;
    }
    video.requestPictureInPicture()
      .then(() => {
        this.emPip.set(true);
        video.addEventListener('leavepictureinpicture', () => this.emPip.set(false), { once: true });
      })
      .catch(() => undefined);
  }

  aoMoverPonteiro(event: PointerEvent): void {
    this.ultimoPonteiro = event.pointerType;
    if (event.pointerType !== 'mouse') return;
    this.mostrarControles();
    this.agendarOcultar();
  }

  aoClicarVideo(event: MouseEvent): void {
    event.stopPropagation();
    if (this.ultimoPonteiro !== 'mouse') {
      if (this.controlesVisiveis() && this.tocando()) {
        this.limparTimer();
        this.controlesVisiveis.set(false);
      } else {
        this.mostrarControles();
        this.agendarOcultar();
      }
      return;
    }
    this.alternar();
  }

  aoPressionarVideo(event: PointerEvent): void {
    this.ultimoPonteiro = event.pointerType;
    event.stopPropagation();
  }

  aoEntrarControles(): void {
    this.sobreControles = true;
    this.limparTimer();
  }

  aoSairControles(): void {
    this.sobreControles = false;
    this.agendarOcultar();
  }

  aoSairPlayer(): void {
    this.posicaoHover.set(null);
    if (this.tocando() && !this.arrastando()) {
      this.limparTimer();
      this.controlesVisiveis.set(false);
    }
  }

  private mostrarControles(): void {
    this.controlesVisiveis.set(true);
  }

  private agendarOcultar(): void {
    this.limparTimer();
    if (!this.tocando() || this.sobreControles) return;
    this.timerOcultar = setTimeout(() => {
      if (this.tocando() && !this.arrastando() && !this.sobreControles) this.controlesVisiveis.set(false);
    }, ESPERA_OCULTAR_MS);
  }

  private limparTimer(): void {
    if (this.timerOcultar) clearTimeout(this.timerOcultar);
    this.timerOcultar = null;
  }

  @HostListener('document:keydown', ['$event'])
  aoTeclar(event: KeyboardEvent): void {
    const alvo = event.target as HTMLElement | null;
    if (alvo?.tagName === 'INPUT' && (alvo as HTMLInputElement).type !== 'range') return;
    const tecla = event.key.toLowerCase();
    if (tecla === ' ' || tecla === 'k') {
      event.preventDefault();
      this.alternar();
      this.mostrarControles();
      this.agendarOcultar();
    } else if (tecla === 'j') {
      this.pular(-PASSO_TECLADO_S);
    } else if (tecla === 'l') {
      this.pular(PASSO_TECLADO_S);
    } else if (tecla === 'm') {
      this.alternarSom(event);
      this.mostrarControles();
      this.agendarOcultar();
    } else if (tecla === 'f') {
      this.alternarTelaCheia();
    }
  }

  formatar(segundos: number): string {
    return MethodsUtils.formatarTempoMidia(segundos);
  }
}
